import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { StyleSheet, View, LayoutChangeEvent } from "react-native";
import { useTheme } from "../hooks/use-theme";
import { LiftTheme } from "../theme";
import { useAtomValue } from "jotai";
import { SnackbarInfo, snackbarStackAtom } from "../atoms/snackbar";
import Animated, {
    Easing,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from "react-native-reanimated";
import { useSnackbar } from "../hooks/snackbar";
import Icon, { IconName } from "./Icon";
import { Gesture, GestureDetector, GestureHandlerRootView } from "react-native-gesture-handler";
import { useSafeAreaFrame, useSafeAreaInsets } from "react-native-safe-area-context";
import Text from "./Text";
import { scheduleOnRN } from "react-native-worklets";
import RootOverlayHost from "./RootOverlayHost";

/**
 * Height assumed for a snackbar that has not been measured yet: it only has to be big enough to
 * park the cell off-screen for the very first frame of the enter animation.
 */
const FALLBACK_SNACKBAR_HEIGHT = 80;
const STACK_GAP = 2;
const ANIMATION_DURATION = 500;

type VisualInfo = {
    color: string;
    backgroundColor: string;
    iconColor?: string;
    iconName?: IconName;
};

const VISUAL_MAP: Record<
    "error" | "primary" | "secondary" | "success" | "info" | "warning",
    (theme: LiftTheme) => VisualInfo
> = {
    primary({ colors }): VisualInfo {
        return {
            backgroundColor: colors.primary,
            color: colors.onPrimary,
        };
    },
    secondary({ colors }): { color: string; backgroundColor: string } {
        return { backgroundColor: colors.secondary, color: colors.onSecondary };
    },
    error({ colors }): VisualInfo {
        return {
            backgroundColor: colors.error,
            color: colors.onError,
            iconName: "snackbar-error",
        };
    },
    info({ colors }): VisualInfo {
        return {
            backgroundColor: colors.primary,
            color: colors.onPrimary,
            iconName: "snackbar-info",
        };
    },
    success({ colors }): VisualInfo {
        return {
            backgroundColor: colors.onSuccessBackground,
            color: colors.successBackground,
            iconName: "check-circle-heavy",
            iconColor: colors.success,
        };
    },
    warning({ colors }): VisualInfo {
        return { backgroundColor: colors.onWarningBackground, color: colors.background };
    },
};

type SnackbarCellProps = {
    snackbar: SnackbarInfo;
    offset: number;
    onChangeHeight: (id: string, height?: number) => void;
};

const SnackbarCell: React.FC<SnackbarCellProps> = ({ snackbar, onChangeHeight, offset = 0 }) => {
    const theme = useTheme();

    const styles = useStyles();
    const { removeSnackbar, dismissSnackbar } = useSnackbar();
    const { color, backgroundColor, iconColor, iconName } = useMemo(() => {
        if (snackbar) {
            const visual = VISUAL_MAP[snackbar.mode || "primary"];
            return visual(theme);
        }
        return { color: "black", backgroundColor: "white" };
    }, [snackbar, theme]);
    const insets = useSafeAreaInsets();

    // The cell is laid out flush with the bottom of the container and only ever moved with a
    // translation: sliding down by the container's own bottom anchor plus the cell height puts it
    // just under the screen edge, whatever the container ends up being anchored to.
    const hiddenOrigin = theme.tabBarHeight + insets.bottom + FALLBACK_SNACKBAR_HEIGHT;
    const hiddenTranslateY = useSharedValue(hiddenOrigin);
    const translateY = useSharedValue(hiddenOrigin);

    const animStyle = useAnimatedStyle(() => {
        return {
            transform: [{ translateY: translateY.value }],
        };
    });

    const actionFinished = useCallback(
        (id: string, isRemoving: boolean, finished?: boolean) => {
            if (finished && isRemoving) {
                onChangeHeight(id, undefined);
                setTimeout(() => removeSnackbar(id), 10);
            }
        },
        [onChangeHeight, removeSnackbar],
    );

    useEffect(() => {
        const id = snackbar._id;
        const isRemoving = !!snackbar._removed;
        if (isRemoving) {
            // remove the snack immediately from offset to start animation
            onChangeHeight(id, undefined);
        }
        translateY.value = withTiming(
            isRemoving ? hiddenTranslateY.value : -offset,
            {
                duration: ANIMATION_DURATION,
                easing: isRemoving ? Easing.back(3) : Easing.elastic(1.5),
            },
            (b) => scheduleOnRN(actionFinished, id, isRemoving, b),
        );
    }, [
        translateY,
        hiddenTranslateY,
        snackbar._id,
        actionFinished,
        offset,
        snackbar._removed,
        onChangeHeight,
    ]);

    const tap = useMemo(() => {
        const _func = () => {
            dismissSnackbar(snackbar._id);
        };
        return Gesture.Tap()
            .runOnJS(true)
            .onFinalize(() => {
                scheduleOnRN(_func);
            });
    }, [dismissSnackbar, snackbar._id]);

    const finalIconName = useMemo(() => {
        return snackbar.icon != undefined ? snackbar.icon : iconName;
    }, [iconName, snackbar.icon]);

    const onLayout = useCallback(
        (event: LayoutChangeEvent) => {
            const { height } = event.nativeEvent.layout;
            // Refine the hiding distance now that the real height is known, so that even a tall
            // multi-line snackbar is fully off-screen once dismissed.
            hiddenTranslateY.value = theme.tabBarHeight + insets.bottom + height;
            onChangeHeight(snackbar._id, height);
        },
        [hiddenTranslateY, insets.bottom, onChangeHeight, snackbar._id, theme.tabBarHeight],
    );

    return (
        <GestureDetector gesture={tap}>
            <Animated.View
                style={[styles.snackbar, animStyle, { backgroundColor }]}
                onLayout={onLayout}
            >
                {finalIconName ? (
                    <Icon
                        size={24}
                        name={finalIconName}
                        style={[styles.icon]}
                        color={iconColor || color}
                    />
                ) : null}
                <Text style={[styles.text, { color }]}>{snackbar.text}</Text>
            </Animated.View>
        </GestureDetector>
    );
};

type OffsetProp = { [key: string]: number };

const SnackBarPortal: React.FC = () => {
    const snackbarStack = useAtomValue(snackbarStackAtom);
    const styles = useStyles();
    const frame = useSafeAreaFrame();
    const [offsets, setOffsets] = useState<OffsetProp>({} as OffsetProp);
    const onChangeHeight = useCallback((id: string, height?: number) => {
        setOffsets((old) => {
            if (height === undefined) {
                const ret = { ...old };
                delete ret[id];
                return ret;
            }
            return { ...old, [id]: height };
        });
    }, []);

    // Mount the overlay only while snackbars are visible: on iOS, RNSFullWindowOverlay attaches
    // its container to the UIWindow when it mounts and never re-raises it, so a permanently
    // mounted overlay ends up BEHIND any native modal presented later. Mounting at show time
    // attaches the container above whatever is currently presented.
    if (snackbarStack.length === 0) {
        return null;
    }

    let offset = 0;
    return (
        // Snackbars are mounted at the root, so on iOS they'd paint BEHIND a presented native modal.
        // RootOverlayHost lifts them into a FullWindowOverlay above the whole VC stack (no-op off iOS).
        // `passthrough` so only the snackbars capture touches and the rest of the screen stays usable.
        <RootOverlayHost pointerMode="passthrough">
            {/* Explicit window size (not absoluteFill) so the bottom-anchored container is placed
                correctly on the very first frame: the overlay is mounted on demand, and on iOS the
                FullWindowOverlay has no size yet at that point — a percentage-based fill would
                collapse to zero height and anchor the snackbars at the TOP of the screen, where
                they would then slide out of view instead of in. box-none keeps empty areas
                touch-transparent. */}
            <GestureHandlerRootView
                pointerEvents="box-none"
                style={[styles.overlay, { width: frame.width, height: frame.height }]}
            >
                <View style={styles.snackbarContainer}>
                    {snackbarStack.reduce((acc, snackbar) => {
                        const cell = (
                            <SnackbarCell
                                key={snackbar._id}
                                snackbar={snackbar}
                                offset={offset}
                                onChangeHeight={onChangeHeight}
                            />
                        );
                        offset += (offsets[snackbar._id] || 0) + STACK_GAP;
                        acc.push(cell);
                        return acc;
                    }, [] as ReactNode[])}
                </View>
            </GestureHandlerRootView>
        </RootOverlayHost>
    );
};

const useStyles = () => {
    const { colors, fonts, gridSize, tabBarHeight } = useTheme();
    const insets = useSafeAreaInsets();
    return useMemo(
        () =>
            StyleSheet.create({
                overlay: {
                    position: "absolute",
                    top: 0,
                    left: 0,
                },
                snackbarContainer: {
                    position: "absolute",
                    // The tab bar is `tabBarHeight` tall on top of the bottom inset, so the
                    // snackbars have to clear both to sit right above it.
                    bottom: tabBarHeight + insets.bottom,
                    paddingTop: 2,
                    paddingHorizontal: 2,
                    left: 0,
                    right: 0,
                    height: "auto",
                    flexDirection: "column",
                    alignItems: "stretch",
                    justifyContent: "flex-end",
                    zIndex: 1000,
                },
                snackbar: {
                    position: "absolute",
                    flexDirection: "row",
                    alignItems: "stretch",
                    // Resting place of the cell; it is only ever moved by `translateY`.
                    bottom: 0,
                    left: 2,
                    right: 2,
                    height: "auto",
                    borderRadius: 8,
                    overflow: "hidden",
                    padding: gridSize / 2,
                },
                icon: {
                    marginRight: gridSize / 2,
                },
                text: {
                    ...fonts.bodyLarge,
                    color: colors.text,
                    flex: 1,
                },
            }),
        [colors.text, fonts, gridSize, insets.bottom, tabBarHeight],
    );
};

export default SnackBarPortal;
